// utils
import { getWidget } from "../shared/utils"
import { study_identification } from "../../dataSchemas/protocol/fieldSchemas"
import { jrctDataSchema } from "../../dataSchemas/protocol/jrctDataSchema"
import { getPreviewStyles } from "./utils/utils"
import { setHiddenFields, hideStudyDetailsKeys } from "./utils/utils"

import { ctgProtocolUISchema } from "./ctgProtocolUISchema"

import { protocolClassNames, firstProtocolSectionClassNames, objectClassNames, arrayClassNames } from "../classes"

const hideFields = {
  root: [
    "type_of_research",
    "study_ipd",
    "study_oversight",
    "sponsors",
    // "milestone_dates",
    "ctis_form",
    "mscs",
    "protocol_id",
    "protocol_information",
    "investigator_information",
    "non_investigator_information",
    "information_on_multicenter_clinical_trials",
    "overview_of_pharmaceuticals",
    "monetary_sponsor",
    "irb_information",
    "secondary_attributes",
  ],
  study_identification: {
    root: ["registered_in_jrct_registry", "summary_of_changes", "acronym", "official_title"],
  },
  contact_information: {
    root: [
      "collaborators",
      "paediatric_investigation_plan",
      "responsible_party",
      "sponsor",
      "investigator_name",
      "investigator_official_title",
      "investigator_affiliation",
      "central_contacts_backup",
      "overall_study_officials",
    ],
    central_contacts: {
      root: [
        "central_contacts_middle_name",
        "central_contacts_degree",
        "central_contacts_phone_number",
        "central_contacts_ext",
      ],
    },
    investigator_contact_details: {
      lead_investigator: {
        root: ["title", "address_4", "functional_contact_point_name"],
      },
    },
  },
  study_timeline: {
    root: [
      "plan_sponsor_audit",
      "compensation_and_medical_care_in_the_event_of_health_damage",
      "record_verification_date",
      "recruitment",
      "milestone_dates",
      "expanded",
      "other_funding_sources",
    ],
  },
  study_eligibility: {
    root: [
      "sex",
      "gender_based",
      "age_limits",
      "accept_healthy_volunteers",
      "is_clinical_study",
      "eligibility_criteria",
      "study_population_description",
      "sampling_method",
      "principle_inclusion_criteria",
      "principle_exlusion_criteria",
    ],
    population_under_study: {
      root: [
        "age_range_ctis",
        "age_range_secondary_ctis",
        "clinical_trial_group",
        "vulnerable_population",
        "recruitment_population_group",
        "subjects_incapable_giving_consent",
        "emergency_situation_description",
        "other_description",
      ],
    },
  },
  study_description: {
    root: [
      "brief_summary_translations",
      "secondary_objective",
      "secondary_objective_translations",
      "therapeutic_area",
      "meddra",
      "trial_scope",
      "ctis_trial_phase",
      "low_intervention_trial",
      "justification_for_low_intervention_trial",
      "principle_exlusion_criteria",
      "principle_inclusion_criteria",
      "protocol_information",
      "scientific_advice_and_pip",
      "sponsors",
      "products",
      "associated_clinical_trials",
      "detailed_description",
      "study_keywords",
      "start_date_of_enrollment",
      "study_start_date_in_japan",
      "inclusion_criteria",
      "exclusion_criteria",
      "discontinuation_criteria",
      "presence_of_interventions",
      "interventions",
      "intervention_code",
      "intervention_keywords",
      "primary_outcome_measures",
      "secondary_outcome_measures",
      "target_disease_code",
      "medical_conditions",
    ],
    medical_conditions: {
      root: ["is_medical_condition_rare", "translations"],
    },
  },
  study_scope: {
    root: [
      "patient_registry",
      "primary_purpose",
      "interventional_study_model",
      "model_description",
      "number_of_arms",
      "masking",
      "masking_description",
      "jrct_masking",
      "enrollment_type",
      "enrollment_type_obs",
      "patient",
      "follow_up_duration",
      "duration_type",
      "additional_patient_registry_data",
      "time_perspective",
      "biospecimen",
      "number_of_cohorts",
      "selection",
      "trial_scope",
      "ctis_trial_phase",
      "periods",
      "countries_outside_eea",
      "control",
      "placebo",
      "blinding",
      "randomization",
      "non_insurance_combined_medical_expenses",
      "countries_of_recruitment_except_japan",
    ],
  },
  study_arms: {
    root: ["arms", "interventions", "crossref", "products"],
  },
  study_outcome_measures: {
    root: ["outcome_measures", "outcome_measures_ctis"],
  },
  study_reference: {
    root: ["associated_clinical_trials", "citations", "links", "available", "study_publications"],
  },
  milestone_dates: {
    root: [...hideStudyDetailsKeys.milestone_dates.root, "lpo", "pcd"],
  },
}

const showFields = {
  study_identification: [
    "brief_title",
    "eupas_registry_number",
    "study_topic_other",
    "darwin_eu_study",
    "study_topic",
    "is_requested_by_regulator",
    "country_regulator_not_listed",
    "is_study_required_by_risk_management_plan",
    "regulatory_procedure_number",
    "other_study_ids",
    "other_study_identification_numbers",
    "emarwd_official_title_and_acronym",
  ],
}

export const eupasProtocolUISchema = (configs) => {
  let preview = configs?.preview
  let formData = configs?.formData
  const disableValidation = configs?.disableValidation || false
  const ctgSchema = ctgProtocolUISchema(configs)

  let returnObj = {
    protocol: {
      "ui:order": [
        "protocol_id",
        "study_type",
        // "eudract_number",
        "study_identification",
        "contact_information",
        "milestone_dates",
        "study_timeline",
        "*",
      ],
      "ui:options": {
        title: "Study Identification and Information",
        classNames: protocolClassNames,
      },
      study_type: {
        "ui:options": {
          title: "Study Type",
          label: false,
          required: true,
          hideFork: true,
          disableValidation,
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      // eudract_number: {
      //   "ui:options": {
      //     title: "EUDRACT number, if available",
      //     placeholder: "(YYYY-NNNNNN-CC)",
      //     label: false,
      //     classNames: {
      //       field: "rjs-col-lg",
      //     },
      //   },
      // },
      study_identification: {
        "ui:order": [
          "protocol_id",
          "emarwd_official_title_and_acronym",
          "brief_title",
          "secondary_ids",
          "is_requested_by_regulator",
          "country_regulator_not_listed",
          "study_topic",
          "study_topic_other",
          "eupas_registry_number",
          "darwin_eu_study",
          "is_study_required_by_risk_management_plan",
          "regulatory_procedure_number",
          "*",
        ],
        "ui:options": {
          classNames: firstProtocolSectionClassNames,
        },
        brief_title: {
          "ui:options": {
            label: false,
            title: "Study description",
            charCountEnabled: true,
            maxChars: 2000,
            required: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        acronym: {
          "ui:options": {
            ...ctgSchema.protocol.study_identification.acronym["ui:options"],
            title: "Study Title Acronym",
            log: true,
            charCountEnabled: true,
            maxChars: 50,
          },
        },
        emarwd_official_title_and_acronym: {
          "ui:options": {
            title: "Official title and acronym",
            label: false,
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 500,
            classNames: {
              fieldContainer: "rjs-full",
            },
            customFnProps: [
              {
                tooltip: "Copy and consolidate CTG Official title and acronym",
                fnKey: "copy-and-consolidate-global-official-title-and-acronym",
              },
            ],
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        is_requested_by_regulator: {
          "ui:options": {
            title: "Was the study required by a regulatory body?",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        country_regulator_not_listed: {
          "ui:options": {
            title: "Other country/ies",
            placeholder: "Specify country/ies not listed",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        is_study_required_by_risk_management_plan: {
          "ui:options": {
            title: "Is the study required by a Risk Management Plan (RMP)?",
            label: false,
            classNames: {
              fieldContainer: "full",
              field: "rjs-col-3xl",
            },
          },
        },
        regulatory_procedure_number: {
          "ui:options": {
            title: "Regulatory procedure number (RMP Category 1 and 2 studies only)",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
            charCountEnabled: true,
            maxChars: 255,
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        other_study_ids: {
          "ui:options": {
            title: "Other Study ID",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
            customFnProps: [
              {
                tooltip: "Copy Secondary ID's from CTG Protocol",
                fnKey: "copy-ctg-protocol-secondary-ids-to-eupas",
              },
            ],
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        other_study_identification_numbers: {
          "ui:options": {
            title: "Other Study ID (links)",
            addButtonTitle: "Add Link",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            url: {
              "ui:options": {
                title: "URL",
                charCountEnabled: true,
                maxChars: 2000,
                label: false,
                tooltip: "The format used should be http://example.com",
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            link: {
              "ui:options": {
                title: "Link text",
                label: false,
                charCountEnabled: true,
                tooltip: "This is the text that will be displayed for the above URL (e.g.: Link to Clinicaltrials.gov)",
                maxChars: 255,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
            },
          },
        },
        eupas_registry_number: {
          "ui:options": {
            title: "EU PAS Registration Number",
            label: false,
            classNames: {
              field: "rjs-col-lg",
            },
          },
        },
        darwin_eu_study: {
          "ui:options": {
            title: "DARWIN EU® study",
            label: false,
            classNames: {
              field: "rjs-col-lg",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        study_topic: {
          "ui:options": {
            title: "Study Topic",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        study_topic_other: {
          "ui:options": {
            title: "Study Topic, if other",
            label: false,
            charCountEnabled: true,
            maxChars: 255,
            classNames: {
              field: "rjs-col-lg",
            },
          },
        },
        ...setHiddenFields(
          Object.keys({
            ...study_identification.properties,
            ...jrctDataSchema.study_identification.clinical_trial_post_marketing_surveillance,
            ...jrctDataSchema.study_identification.shared,
            ...hideFields.study_identification.root,
          }),
          showFields.study_identification
        ),
        ...setHiddenFields(hideFields.study_identification.root),
      },
      contact_information: {
        "ui:order": [
          "investigator_contact_details",
          "central_contacts",
          "collaborators",
          "investigator_center",
          "encepp_center_organization_affiliation",
          "encepp_center_website",
          "*",
          "networks_registered_encepp",
          "other_networks",
          "centers_registered_encepp",
          "other_centers",
          "country_study_is_conducted",
        ],
        "ui:options": {
          title: "Study Sponsor, Collaborators and Contact Information",
          classNames: objectClassNames.primary,
        },
        central_contacts: {
          ...ctgSchema.protocol.contact_information.central_contacts,
          "ui:options": {
            ...ctgSchema.protocol.contact_information.central_contacts["ui:options"],
            title: "Study institution contact",
          },
          central_contacts_first_name: {
            ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_first_name,
            "ui:options": {
              ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_first_name["ui:options"],
              charCountEnabled: true,
              maxChars: 255,
              required: true,
            },
          },
          central_contacts_last_name: {
            ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_last_name,
            "ui:options": {
              ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_last_name["ui:options"],
              charCountEnabled: true,
              maxChars: 255,
              required: true,
            },
          },
          central_contacts_email: {
            ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_email,
            "ui:options": {
              ...ctgSchema.protocol.contact_information.central_contacts.central_contacts_email["ui:options"],
              charCountEnabled: true,
              maxChars: 255,
              required: true,
            },
          },
          ...setHiddenFields(hideFields.contact_information.central_contacts.root),
        },
        institution_conducting_study: {
          "ui:options": {
            title: "Institution conducting the study",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "Lookahead", preview }),
        },
        institution_conducting_study_if_not_in_list: {
          "ui:options": {
            title: "Institution conducting the study if not in the list",
            label: false,
            charCountEnabled: true,
            maxChars: 255,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
        },
        additional_institutions: {
          "ui:options": {
            title: "Additional institutions",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "Lookahead", preview }),
        },
        additional_institutions_if_not_in_list: {
          "ui:options": {
            title: "Additional institutions if not in the list",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
        },
        investigator_center: {
          "ui:options": {
            title: "Center to which the investigator belongs",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "Lookahead", preview }),
        },
        department_research_group: {
          "ui:options": {
            title: "Department/Research group",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        encepp_center_organization_affiliation: {
          "ui:options": {
            title: "Organisation/affiliation",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        encepp_center_website: {
          "ui:options": {
            title: "Website/Homepage",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        networks_registered_encepp: {
          "ui:options": {
            title: "Network conducting the study",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "Lookahead", preview }),
        },
        other_networks: {
          "ui:options": {
            title: "Additional networks if not in the list",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        centers_registered_encepp: {
          "ui:options": {
            title: "Centers registered with ENCePP",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        other_centers: {
          "ui:options": {
            title: "Other Centers",
            addButtonTitle: "Add Center",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            name: {
              "ui:options": {
                label: false,
                title: "Name",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            location: {
              "ui:options": {
                label: false,
                title: "Location",
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
          },
        },
        country_study_is_conducted: {
          "ui:options": {
            title: "Study countries",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        investigator_contact_details: {
          "ui:options": {
            // title: "EMA RWD Contact Information",
            classNames: {
              objectTitle: "rjs-h2",
              fieldContainer: "rjs-full",
            },
          },
          lead_investigator: {
            "ui:options": {
              title: "Primary Lead Investigator",
              classNames: objectClassNames.contact_root_container,
            },
            // title: {
            //   "ui:options": {
            //     label: false,
            //     required: true,
            //     disableValidation,
            //     title: "Title",
            //     classNames: {
            //       fieldContainer: "rjs-col-sm",
            //     },
            //   },
            // },
            first_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                title: "First Name",
                charCountEnabled: true,
                maxChars: 255,
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            last_name: {
              "ui:options": {
                label: false,
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 255,
                title: "Last Name",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            orcid: {
              "ui:options": {
                label: false,
                disableValidation,
                charCountEnabled: true,
                maxChars: 19,
                title: "ORCID",
                tooltip: "Enter the ORCID in the format XXXX-XXXX-XXXX-XXXX",
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
                validations: [
                  {
                    // regex: /^(?!.*\b\w{4}-\w{4}-\w{4}-\w{4}\b).*$/,
                    regex: /^\b\w{4}-\w{4}-\w{4}-\w{4}\b$/,
                    message: "Field is required to be in the following format: XXXX-XXXX-XXXX-XXXX",
                  },
                ],
              },
            },
            ...setHiddenFields(hideFields.contact_information.investigator_contact_details.lead_investigator.root),
          },
        },
        ...setHiddenFields(hideFields.contact_information.root),
      },
      milestone_dates: {
        "ui:options": {
          title: "Study Timelines and Status",
          classNames: objectClassNames.primary,
        },
        fpi: {
          "ui:options": {
            title: "Study Start Date",
            classNames: objectClassNames.pactdate,
            translations: {
              jp: {
                title: "実施期間（終了日）",
              },
            },
          },
          baseline: {
            "ui:options": {
              title: "Baseline",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              required: true,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
              translations: {
                jp: {
                  title: "現在の",
                },
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        ...setHiddenFields(hideFields.milestone_dates.root),
      },
      study_timeline: {
        // "ui:options": {
        //   title: "Study Timelines and Status",
        //   classNames: objectClassNames.primary,
        // },
        signed_funding_contract_date: {
          "ui:options": {
            title: "Date when funding contract was signed",
            required: true,
            label: false,
            classNames: objectClassNames.dateXl,
          },
          planned: {
            "ui:options": {
              title: "Planned",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        // data_collection_start_date: {
        //   "ui:options": {
        //     title: "Study start date",
        //     required: true,
        //     disableValidation,
        //     classNames: objectClassNames.dateXl,
        //   },
        //   planned: {
        //     "ui:options": {
        //       title: "Planned",
        //       label: false,
        //       classNames: {
        //         field: "rjs-col-date",
        //       },
        //     },
        //     "ui:widget": getWidget({ type: "date", preview }),
        //   },
        //   actual: {
        //     "ui:options": {
        //       title: "Actual",
        //       label: false,
        //       classNames: {
        //         field: "rjs-col-date",
        //       },
        //     },
        //     "ui:widget": getWidget({ type: "date", preview }),
        //   },
        // },
        data_analysis_start_date: {
          "ui:options": {
            title: "Data analysis start date",
            required: true,
            disableValidation,
            classNames: objectClassNames.dateXl,
          },
          planned: {
            "ui:options": {
              title: "Planned",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        interim_report_date: {
          "ui:options": {
            title: "Date of interim report, if expected",
            classNames: objectClassNames.dateXl,
          },
          planned: {
            "ui:options": {
              title: "Planned",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        final_study_report_date: {
          "ui:options": {
            title: "Date of final study report",
            required: true,
            disableValidation,
            classNames: objectClassNames.dateXl,
          },
          planned: {
            "ui:options": {
              title: "Planned",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        more_details_sources_of_funding: {
          "ui:options": {
            title: "More details on source of funding",
            label: false,
            tooltip: "If source funding is a pharmaceutical company, company name should be added in this field",
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ...setHiddenFields(hideFields.study_timeline.root),
      },
      study_description: {
        "ui:order": ["brief_summary", "detailed_description", "*"],
        "ui:options": {
          title: "Study Description and Conditions",
          classNames: objectClassNames.primary,
        },
        brief_summary: {
          ...ctgSchema.protocol.study_description.brief_summary,
          "ui:options": {
            ...ctgSchema.protocol.study_description.brief_summary["ui:options"],
            title: "Main study objective",
            maxChars: 10000,
          },
        },
        // medical_conditions: {
        //   "ui:options": {
        //     title: "Medical condition to be studied",
        //     addButtonTitle: "Add Medical Condition",
        //     label: false,
        //     classNames: arrayClassNames.primary,
        //   },
        //   items: {
        //     condition: {
        //       "ui:options": {
        //         title: "Condition",
        //         label: false,
        //         classNames: {
        //           fieldContainer: "rjs-full",
        //         },
        //       },
        //     },
        //     ...setHiddenFields(hideFields.study_description.medical_conditions.root),
        //   },
        // },
        study_conditions: {
          ...ctgSchema.protocol.study_description.study_conditions,
          "ui:options": {
            ...ctgSchema.protocol.study_description.study_conditions["ui:options"],
            title: "Medical condition to be studied",
          },
        },
        additional_medical_conditions: {
          "ui:options": {
            title: "Additional Medical Condition(s)",
            label: false,
            tooltip:
              "If none of the above terms are applicable, please use this field to describe the (additional) medical condition studied.",
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ...setHiddenFields(hideFields.study_description.root),
      },
      study_scope: {
        "ui:order": [
          "enrollment_number",
          "scope_of_study",
          "other_scope_description",
          "observational_study_model",
          "other_non_interventional_study_design",
          "*",
        ],
        "ui:options": {
          title: "Study Scope and Design",
          classNames: objectClassNames.primary,
        },
        enrollment_number: {
          "ui:options": {
            title: "Estimated number of subjects",
            label: false,
            required: true,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        scope_of_study: {
          "ui:options": {
            title: "Scope of the study",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-2xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        study_phase: {
          ...ctgSchema.protocol.study_scope.study_phase,
          "ui:options": {
            ...ctgSchema.protocol.study_scope.study_phase["ui:options"],
            title: "Clinical Trial Phase",
            required: false,
          },
        },
        observational_study_model: {
          ...ctgSchema.protocol.study_scope.observational_study_model,
          "ui:options": {
            ...ctgSchema.protocol.study_scope.observational_study_model["ui:options"],
            title: "Non-interventional study design",
          },
        },
        other_non_interventional_study_design: {
          "ui:options": {
            title: "Non-interventional study design, other",
            label: false,
            charCountEnabled: true,
            maxChars: 2000,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        other_scope_description: {
          "ui:options": {
            title: "If other, further details on the scope of the study",
            label: false,
            charCountEnabled: true,
            maxChars: 255,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        design_of_study: {
          "ui:options": {
            title: "Study Design",
            label: false,
            charCountEnabled: true,
            maxChars: 300,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        clinical_trial_regulatory_scope: {
          "ui:options": {
            title: "Clinical trial regulatory scope",
            label: false,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        clinical_trial_type: {
          "ui:options": {
            title: "Clinical trial type",
            label: false,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        data_collection_method: {
          "ui:options": {
            title: "Data collection method",
            label: false,
            orientation: "vertical",
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        allocation: {
          ...ctgSchema.protocol.study_scope.allocation,
          "ui:options": {
            ...ctgSchema.protocol.study_scope.allocation["ui:options"],
            required: false,
            title: "Clinical trial randomisation",
          },
        },
        summary_results: {
          "ui:options": {
            label: false,
            title: "Summary Results",
            charCountEnabled: true,
            maxChars: 2000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ...setHiddenFields(hideFields.study_scope.root),
      },
      study_arms: {
        "ui:options": {
          title: "Study Drug or IMP Information",
          classNames: objectClassNames.primary,
        },
        setting: {
          "ui:options": {
            title: "Setting",
            label: false,
            tooltip:
              "Setting in terms of persons, place, time period and selection criteria, including a split by treatment arms/comparators or other relevant variable",
            charCountEnabled: true,
            maxChars: 2000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        eupas_interventions: {
          "ui:options": {
            title: "Interventions",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        comparators: {
          "ui:options": {
            title: "Comparators",
            label: false,
            charCountEnabled: true,
            maxChars: 2000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        medicine_name: {
          "ui:options": {
            title: "Name of medicine",
            tooltip: "Brand names of the medicines studied",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        other_medicine_name: {
          "ui:options": {
            title: "Name of medicine, other",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
        },
        substance_inns: {
          "ui:options": {
            title: "Study drug International non-proprietary name (INN) or common name",
            label: false,
            classNames: {
              field: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        atc_code: {
          "ui:options": {
            title: "Anatomical Therapeutic Chemical (ATC) code",
            label: false,
            classNames: {
              field: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        ...setHiddenFields(hideFields.study_arms.root),
      },
      study_eligibilty: {
        "ui:order": ["*", "sources_of_data", "other_data_source", "registered_data_sources", "registered_data_sources"],
        "ui:options": {
          title: "Eligibility",
          classNames: objectClassNames.primary,
        },
        population_under_study: {
          "ui:options": {
            title: "Population under study",
            label: false,
            classNames: objectClassNames.tertiary,
          },
          age: {
            "ui:options": {
              title: "Age",
              required: true,
              disableValidation,
              label: false,
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          other_population: {
            "ui:options": {
              title: "Other population",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          ...setHiddenFields(hideFields.study_eligibility.population_under_study.root),
        },
        age_groups: {
          "ui:options": {
            title: "Age Groups",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        special_interest_of_population: {
          "ui:options": {
            title: "Special population of interest",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        other_special_interest_of_population: {
          "ui:options": {
            title: "Special population of interest, other",
            label: false,
            charCountEnabled: true,
            maxChars: 255,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        short_description_of_study_population: {
          "ui:options": {
            title: "Short description of the study population",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
            charCountEnabled: true,
            maxChars: 2000,
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ...setHiddenFields(hideFields.study_eligibility.root),
      },
      study_outcome_measures: {
        "ui:options": {
          title: "Study Endpoints / Outcome Measures",
          classNames: objectClassNames.primary,
        },
        eupas_outcomes: {
          "ui:options": {
            title: "Outcomes",
            label: false,
            charCountEnabled: true,
            maxChars: 2000,
            classNames: {
              fieldContainer: "rjs-full",
            },
            customFnProps: [
              {
                tooltip: "Copy and consolidate outcome measures from CTG Protocol",
                fnKey: "copy-and-consolidate-ctg-protocol-om-to-eupas",
              },
            ],
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        statistical_analysis_plan: {
          "ui:options": {
            title: "Data analysis plan",
            tooltip:
              "Brief summary of the analysis method (e.g. risk estimation, measures of risk, internal/external validity)",
            label: false,
            disableValidation,
            charCountEnabled: true,
            maxChars: 2000,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        ...setHiddenFields(hideFields.study_outcome_measures.root),
      },
      study_reference: {
        "ui:options": {
          title: "References",
          classNames: objectClassNames.primary,
        },
        is_requesting_encepp_seal: {
          "ui:options": {
            title: "Are you requesting the ENCePP seal for this study?",
            label: false,
            tooltip: "See information on the ENCePP Seal and the requirements for application",
            tooltipUrl: "https://www.encepp.europa.eu/encepp-tools/encepp-seal_en",
            tooltipUrlTitle: "Link",
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        encepp_code_of_conduct: {
          "ui:options": {
            title: "ENCePP Code of conduct (Is this study performed in line with the ENCePP Code of conduct?)",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        ...setHiddenFields(hideFields.study_reference.root),
      },
      ema_hma_additional_information: {
        "ui:options": {
          title: "EMA HMA Catalogue Additional Information",
          classNames: objectClassNames.primary,
        },
        data_sources_used_converted_to_cdm: {
          "ui:options": {
            title: "Were the data sources used in the study converted (ETL-ed) to a CDM (common data model)?",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        cdm_mapping: {
          "ui:options": {
            title: "Study CDM Mapping",
            itemTitle: "Identifier",
            addButtonTitle: "Add Study CDM Mapping",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
          },
          items: {
            cdm_names: {
              "ui:options": {
                title: "CDM name",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            other_cdm_name: {
              "ui:options": {
                title: "CDM Name, other",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            cdm_version: {
              "ui:options": {
                title: "CDM version",
                label: false,
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
          },
        },
        data_quality_specifications: {
          "ui:options": {
            title: "Data quality specifications",
            classNames: objectClassNames.tertiary,
          },
          check_conformance: {
            "ui:options": {
              title: "Check conformance",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            check_conformance_of_data_completed: {
              "ui:options": {
                title:
                  "Was a check of the conformance of data (i.e., data are in the correct format/syntax) completed?",
                label: false,
                required: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
          check_completeness: {
            "ui:options": {
              title: "Check completeness",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            check_completeness_of_data_completed: {
              "ui:options": {
                title: "Was a check of the completeness of data completed?",
                label: false,
                required: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
          check_stability: {
            "ui:options": {
              title: "Check stability",
              label: false,
              required: true,
              classNames: objectClassNames.tertiary,
            },
            check_stability_of_data_over_time_completed: {
              "ui:options": {
                title: "Was a check of the stability of data (e.g. codes) over time completed?",
                label: false,
                required: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
          check_logical_consistency: {
            "ui:options": {
              title: "Check logical consistency",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            check_logical_consistency_of_data_completed: {
              "ui:options": {
                title: "Was a check of logical consistency of data completed?",
                label: false,
                required: true,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
        },
        data_characterisation: {
          "ui:options": {
            title: "Data characterisation",
            label: false,
            classNames: objectClassNames.tertiary,
          },
          "ui:order": [
            "data_characterisation_conducted",
            "data_characterisation_moment",
            "data_characterisation_detail",
            "data_characterisation_detail_link",
            "procedure_of_data_extraction",
            "procedure_of_results_generation",
            "procedure_of_data_quality_assurance",
            "procedure_of_data_quality_assurance_detail_link",
            "procedure_of_data_quality_assurance_detail_link",
          ],
          data_characterisation_conducted: {
            "ui:options": {
              title: "Data characterisation conducted",
              label: false,
              tooltip: "Was a data characterisation or quality check process completed?",
              required: true,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          data_characterisation_moment: {
            "ui:options": {
              title: "Data characterisation moment",
              label: false,
              tooltip:
                "At what stages of the study were data characterisation steps or quality checks implemented? Select all that apply",
              classNames: {
                fieldContainer: "rjs-col-2xl",
              },
            },
            "ui:widget": getWidget({ type: "MultiSelect", preview }),
          },
          data_characterisation_detail: {
            "ui:options": {
              title: "Data characterisation details",
              label: false,
              tooltip: "Provide a summary description of the data characterisation or quality check process",
              charCountEnabled: true,
              maxChars: 1000,
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
          data_characterisation_detail_link: {
            "ui:options": {
              title: "Data characterisation details (link)",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            url: {
              "ui:options": {
                title: "URL",
                label: false,
                tooltip: "This must be an external URL such as http://example.com.",
                charCountEnabled: true,
                maxChars: 2048,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            link_text: {
              "ui:options": {
                title: "Link Text",
                label: false,
                charCountEnabled: true,
                maxChars: 255,
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
            },
          },
          procedure_of_data_extraction: {
            "ui:options": {
              title: "Procedure of data extraction",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            url: {
              "ui:options": {
                title: "URL",
                label: false,
                tooltip: "This must be an external URL such as http://example.com.",
                charCountEnabled: true,
                maxChars: 2048,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            link_text: {
              "ui:options": {
                title: "Link Text",
                label: false,
                charCountEnabled: true,
                maxChars: 255,
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
            },
          },
          procedure_of_results_generation: {
            "ui:options": {
              title: "Procedure of results generation",
              label: false,
              classNames: objectClassNames.tertiary,
            },
            url: {
              "ui:options": {
                title: "URL",
                label: false,
                charCountEnabled: true,
                maxChars: 2048,
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            link_text: {
              "ui:options": {
                title: "Link Text",
                label: false,
                charCountEnabled: true,
                maxChars: 255,
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
            },
          },
        },
        source_of_funding: {
          "ui:options": {
            title: "Source of funding",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        registered_data_sources: {
          "ui:options": {
            title: "Data source(s) ",
            label: false,
            classNames: {
              fieldContainer: "rjs-col-2xl",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        other_registered_data_sources: {
          "ui:options": {
            title: "Data source(s), other",
            label: false,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        sources_of_data: {
          "ui:options": {
            title: "Data sources (types)",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-col-1-3",
            },
          },
          "ui:widget": getWidget({ type: "MultiSelect", preview }),
        },
        other_data_source: {
          "ui:options": {
            title: "Data sources (types), other",
            label: false,
            disableValidation,
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
      },
      ...setHiddenFields(hideFields.root),
      ...setHiddenFields(hideStudyDetailsKeys.root),
    },
  }

  if (preview === true) {
    getPreviewStyles(returnObj, formData)
  }

  return returnObj
}
